import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { rupiah, formatWIB, wibNowLocal, wibLocalToUTCISO, toNum } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { MoneyInput } from "@/components/MoneyInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { CustomerForm } from "@/pages/Customers";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import { Zap, Sparkles, Settings2, Plus, Pencil, Trash2, Layers, UserPlus, AlertTriangle, Wrench, FileSpreadsheet, Download, Upload, CheckCircle2, XCircle, PanelRight, PanelBottom } from "lucide-react";

const METHODS = [
  { v: "tunai", l: "Tunai" }, { v: "transfer", l: "Transfer" },
  { v: "qris", l: "QRIS" }, { v: "campuran", l: "Campuran" },
];
const methodLabel = (v) => METHODS.find((m) => m.v === v)?.l || v;

// Deteksi akhir pekan (Sabtu/Minggu) dalam zona WIB dari tanggal ISO UTC
const isWeekendWIB = (iso) => {
  try {
    const dow = new Date(new Date(iso).getTime() + 7 * 3600 * 1000).getUTCDay(); // 0=Min, 6=Sab
    return dow === 0 || dow === 6;
  } catch { return false; }
};
const BULAN_ID_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const monthKeyWIB = (iso) => {
  try {
    const d = new Date(new Date(iso).getTime() + 7 * 3600 * 1000);
    return `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
  } catch { return ""; }
};
const monthLabelWIB = (iso) => {
  try {
    const d = new Date(new Date(iso).getTime() + 7 * 3600 * 1000);
    return `${BULAN_ID_FULL[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  } catch { return "-"; }
};
const HARI_ID_FULL = ["Minggu", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const dayKeyWIB = (iso) => {
  try {
    const d = new Date(new Date(iso).getTime() + 7 * 3600 * 1000);
    return `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`;
  } catch { return ""; }
};
const dayLabelWIB = (iso) => {
  try {
    const d = new Date(new Date(iso).getTime() + 7 * 3600 * 1000);
    return `${HARI_ID_FULL[d.getUTCDay()]}, ${d.getUTCDate()} ${BULAN_ID_FULL[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  } catch { return "-"; }
};
// Pemilih bulan (input type=month => "YYYY-MM", 1-based) <-> kunci grup WIB ("YYYY-Midx", 0-based)
const nowMonthInput = () => {
  const d = new Date(new Date().getTime() + 7 * 3600 * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthInputToKey = (mi) => {
  const [y, m] = (mi || "").split("-").map(Number);
  if (!y || !m) return "";
  return `${y}-${m - 1}`;
};
// Badge pembeda Tarik vs Kirim (dipakai di tabel)
const flowBadge = (flow) => {
  const f = (flow || "").toLowerCase();
  if (f === "tarik") return <Badge className="text-[10px] border-transparent bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">Tarik</Badge>;
  if (f === "kirim") return <Badge className="text-[10px] border-transparent bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300">Kirim</Badge>;
  if (f === "bonus") return <Badge className="text-[10px] border-transparent bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">Bonus</Badge>;
  return null;
};
const flowRowClass = (r) => {
  const f = (r.flow || "").toLowerCase();
  if (f === "tarik") return "bg-emerald-50/60 hover:bg-emerald-50 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/15";
  if (f === "kirim") return "bg-sky-50/60 hover:bg-sky-50 dark:bg-sky-500/10 dark:hover:bg-sky-500/15";
  if (f === "bonus") return "bg-violet-50/60 hover:bg-violet-50 dark:bg-violet-500/10 dark:hover:bg-violet-500/15";
  return "";
};

export default function Transactions() {
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [monthSel, setMonthSel] = useState(nowMonthInput());
  const [allMode, setAllMode] = useState(false);
  const [apps, setApps] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(params.get("new") === "1");
  // Posisi panel form (khusus desktop): "right" = belah kanan, "bottom" = belah bawah.
  const [dockPos, setDockPos] = useState(() => localStorage.getItem("trxDockPos") || "right");
  const setDock = (p) => { setDockPos(p); localStorage.setItem("trxDockPos", p); };
  // Ukuran panel bisa di-drag (desktop): lebar utk kanan, tinggi utk bawah. Ada batas min/max.
  const DOCK_W_MIN = 360, DOCK_H_MIN = 220;
  const [dockW, setDockW] = useState(() => { const v = Number(localStorage.getItem("trxDockW")); return v >= DOCK_W_MIN ? v : 520; });
  const [dockH, setDockH] = useState(() => { const v = Number(localStorage.getItem("trxDockH")); return v >= DOCK_H_MIN ? v : Math.round((typeof window !== "undefined" ? window.innerHeight : 800) * 0.62); });
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => { localStorage.setItem("trxDockW", String(dockW)); }, [dockW]);
  useEffect(() => { localStorage.setItem("trxDockH", String(dockH)); }, [dockH]);
  // Ukur tinggi aktual panel (mode bawah auto-fit isi) supaya padding tabel pas & tak tertutup.
  const panelRef = useRef(null);
  const [panelH, setPanelH] = useState(0);
  useEffect(() => {
    if (!open || !isDesktop) { setPanelH(0); return; }
    const el = panelRef.current;
    if (!el) return;
    const update = () => setPanelH(el.offsetHeight);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, [open, isDesktop, dockPos]);
  const startResize = (e) => {
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY, sw = dockW, sh = dockH;
    const move = (ev) => {
      if (dockPos === "right") {
        setDockW(Math.max(DOCK_W_MIN, Math.min(sw + (sx - ev.clientX), Math.round(window.innerWidth * 0.7))));
      } else {
        setDockH(Math.max(DOCK_H_MIN, Math.min(sh + (sy - ev.clientY), Math.round(window.innerHeight * 0.85))));
      }
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); document.body.style.userSelect = ""; };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const [manage, setManage] = useState(false);
  const [openImport, setOpenImport] = useState(false);

  const appName = (id) => apps.find((a) => a.id === id)?.name || "—";

  const loadCatalog = () => Promise.all([
    api.get("/catalog/tabs").then((r) => setTabs(r.data)),
    api.get("/catalog/categories").then((r) => setCats(r.data)),
  ]);
  const loadTrx = () => {
    let params = { limit: 5000 };
    if (!allMode && monthSel) {
      const [y, m] = monthSel.split("-").map(Number);
      // Padding ±1 hari agar aman terhadap selisih zona waktu (WIB vs UTC); difilter presisi di klien.
      const date_from = new Date(Date.UTC(y, m - 1, 1) - 24 * 3600 * 1000).toISOString();
      const date_to = new Date(Date.UTC(y, m, 1) + 24 * 3600 * 1000).toISOString();
      params = { ...params, date_from, date_to };
    }
    return api.get("/transactions", { params }).then((r) => setList(r.data));
  };
  const load = () => {
    loadTrx();
    api.get("/apps").then((r) => setApps(r.data));
    loadCatalog();
  };
  useEffect(() => { api.get("/apps").then((r) => setApps(r.data)); loadCatalog(); }, []);
  // Muat ulang transaksi tiap kali bulan / mode "semua" berubah
  useEffect(() => { loadTrx(); /* eslint-disable-next-line */ }, [monthSel, allMode]);

  // Baris yang tampil: presisi ke bulan WIB terpilih (kecuali mode "semua")
  const visibleList = useMemo(() => {
    if (allMode) return list;
    const key = monthInputToKey(monthSel);
    return list.filter((r) => monthKeyWIB(r.date) === key);
  }, [list, allMode, monthSel]);

  const [editingTrx, setEditingTrx] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  // Edit dibuka di POP-UP terpisah — TIDAK menyentuh form transaksi baru (open/rapid) yang mungkin sedang berjalan.
  const openEdit = (trx) => { setEditingTrx(trx); setEditOpen(true); };
  const closeEdit = () => { setEditOpen(false); setEditingTrx(null); };

  const columns = useMemo(() => [
    { key: "date", label: "Tanggal & Jam", sortable: true, sortValue: (r) => r.date || "", render: (r) => (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-sm">{formatWIB(r.date)}</span>
        {isWeekendWIB(r.date) && <Badge className="text-[9px] border-transparent bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">Akhir Pekan</Badge>}
      </span>
    ) },
    { key: "tab_name", label: "Tab", sortable: true, filter: true, render: (r) => <Badge variant="secondary">{r.tab_name || "-"}</Badge> },
    { key: "category_name", label: "Kategori", sortable: true, filter: true, render: (r) => (
      <span className="inline-flex items-center gap-1.5 flex-wrap">
        {flowBadge(r.flow)}
        <span>{r.category_name || r.kind || "-"}</span>
        {r.is_operational && <Badge className="text-[10px] border-transparent bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">Operasional{r.operational_note ? ` · ${r.operational_note}` : ""}</Badge>}
        {r.is_debt && <Badge variant="destructive" className="text-[10px]">Utang {rupiah(r.debt_amount || 0)}</Badge>}
        {r.affect_balance === false && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">Data lama</Badge>}
      </span>
    ) },
    { key: "app_id", label: "Aplikasi", sortable: true, filter: true, filterValue: (r) => appName(r.app_id), sortValue: (r) => appName(r.app_id), render: (r) => (
      <span>{appName(r.app_id)}</span>
    ) },
    { key: "account_label", label: "Akun", sortable: true, filter: true, filterValue: (r) => r.account_label || "", sortValue: (r) => r.account_label || "", render: (r) => (
      r.account_label
        ? <Badge variant="outline" className="text-[10px] font-normal">{r.account_label}</Badge>
        : <span className="text-xs text-muted-foreground">-</span>
    ) },
    { key: "nominal", label: "Nominal", sortable: true, align: "right", sortValue: (r) => Number(r.nominal || r.harga_jual || 0), render: (r) => {
      const f = (r.flow || "").toLowerCase();
      const cls = f === "tarik" ? "text-emerald-600 dark:text-emerald-400 font-semibold"
        : f === "kirim" ? "text-sky-600 dark:text-sky-400 font-semibold"
        : f === "bonus" ? "text-violet-600 dark:text-violet-400 font-semibold" : "";
      return <span className={`money ${cls}`}>{rupiah(r.nominal || r.harga_jual || 0)}</span>;
    } },
    { key: "potongan", label: "Potongan", sortable: true, align: "right", sortValue: (r) => Number(r.potongan || 0), render: (r) => (
      Number(r.potongan) > 0
        ? <span className="money text-rose-600 dark:text-rose-400">-{rupiah(r.potongan)}</span>
        : <span className="text-muted-foreground">-</span>
    ) },
    { key: "keuntungan", label: "Untung", sortable: true, align: "right", sortValue: (r) => Number(r.keuntungan || 0), render: (r) => <span className="money text-emerald-600 dark:text-emerald-400 font-semibold">+{rupiah(r.keuntungan || 0)}</span> },
    { key: "payment_method", label: "Metode", sortable: true, filter: true, filterValue: (r) => r.transfer_method || (r.payment_method ? methodLabel(r.payment_method) : "-"), render: (r) => (
      r.transfer_method
        ? <span className="text-sm">{r.transfer_method}</span>
        : (r.payment_method ? <span className="text-sm">{methodLabel(r.payment_method)}</span> : <span className="text-xs text-muted-foreground">-</span>)
    ) },
    { key: "notes", label: "Catatan", render: (r) => <span className="text-xs text-muted-foreground">{r.notes || "-"}</span> },
    { key: "_act", label: "", render: (r) => (
      <div className="flex items-center gap-0.5">
        <Button variant="ghost" size="icon" data-testid={`btn-edit-trx-${r.id}`} title="Edit" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button variant="ghost" size="icon" data-testid={`btn-delete-trx-${r.id}`} title="Hapus" onClick={async () => {
          if (!(await confirmDialog({ title: "Hapus transaksi ini?" }))) return;
          await api.delete(`/transactions/${r.id}`); toast.success("Transaksi dihapus"); load();
        }}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
    ) },
  ], [apps]);

  const closeForm = () => { setOpen(false); setParams({}); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Riwayat & Input</p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Transaksi</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="btn-import-excel" variant="outline" className="gap-2 h-11" onClick={() => setOpenImport(true)}>
            <FileSpreadsheet className="h-4 w-4" /> Import Excel
          </Button>
          <Button data-testid="btn-manage-catalog" variant="outline" className="gap-2 h-11" onClick={() => setManage(true)}>
            <Settings2 className="h-4 w-4" /> Kelola Tab & Kategori
          </Button>
          <Button data-testid="btn-new-transaction" size="lg" className="gap-2 h-11" onClick={() => { setOpen(true); setParams({ new: "1" }); }}>
            <Zap className="h-4 w-4" /> Transaksi Baru
          </Button>
        </div>
      </div>

      <div style={isDesktop && open ? (dockPos === "bottom" ? { paddingBottom: (panelH || 0) + 8 } : { paddingRight: dockW + 8 }) : undefined}>
        <Card className="p-4">
          <div className="flex items-center gap-2 flex-wrap mb-3 pb-3 border-b">
            <Label className="text-xs text-muted-foreground">Bulan</Label>
            <input
              type="month"
              data-testid="trx-month"
              value={monthSel}
              disabled={allMode}
              onChange={(e) => setMonthSel(e.target.value || nowMonthInput())}
              className="h-9 rounded-md border bg-background px-2 text-sm disabled:opacity-40"
            />
            <Button variant={allMode ? "default" : "outline"} size="sm" className="h-9" data-testid="trx-all-months" onClick={() => setAllMode((v) => !v)}>
              {allMode ? "Menampilkan semua" : "Semua bulan"}
            </Button>
            <span className="text-xs text-muted-foreground ml-auto">{visibleList.length} transaksi{allMode ? " (semua)" : ""}</span>
          </div>
          <DataTable columns={columns} rows={visibleList} testid="trx-table" zoomable rowClassName={flowRowClass} groupBy={{ key: (r) => monthKeyWIB(r.date), label: (r) => monthLabelWIB(r.date), subKey: (r) => dayKeyWIB(r.date), subLabel: (r) => dayLabelWIB(r.date) }} emptyText='Belum ada transaksi. Klik "Transaksi Baru".' />
        </Card>

        {open && (
          <div
            ref={panelRef}
            className={
              dockPos === "bottom"
                ? "fixed z-40 left-0 right-0 top-14 bottom-14 lg:left-64 lg:right-0 lg:top-auto lg:bottom-0"
                : "fixed z-40 left-0 right-0 top-14 bottom-14 lg:left-auto lg:right-0 lg:top-14 lg:bottom-0"
            }
            style={isDesktop ? (dockPos === "bottom" ? { height: dockH } : { width: dockW }) : undefined}
            data-testid="trx-docked-panel"
          >
            {/* Handle geser ukuran (desktop): mode kanan = lebar, mode bawah = tinggi */}
            <div
              onPointerDown={startResize}
              data-testid="trx-dock-resize"
              className={
                dockPos === "bottom"
                  ? "hidden lg:flex items-center justify-center absolute inset-x-0 top-0 h-2 cursor-row-resize bg-border/40 hover:bg-primary/50 z-20 group"
                  : "hidden lg:flex items-center justify-center absolute inset-y-0 left-0 w-2 cursor-col-resize bg-border/40 hover:bg-primary/50 z-20 group"
              }
              title={dockPos === "bottom" ? "Geser untuk atur tinggi" : "Geser untuk atur lebar"}
            >
              <span className={dockPos === "bottom" ? "h-0.5 w-8 rounded-full bg-muted-foreground/50 group-hover:bg-primary" : "w-0.5 h-8 rounded-full bg-muted-foreground/50 group-hover:bg-primary"} />
            </div>

            <div className={`relative flex flex-col h-full overflow-hidden bg-card border-border rounded-xl shadow-2xl lg:rounded-none lg:shadow-none ${dockPos === "bottom" ? "lg:border-t lg:pt-2" : "lg:border-l lg:pl-2"}`}>
              <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b shrink-0">
                <h2 className="text-lg font-bold">Transaksi Baru</h2>
                <div className="flex items-center gap-1">
                  <div className="hidden lg:flex items-center rounded-md border p-0.5 mr-1" data-testid="trx-dock-toggle">
                    <Button type="button" variant={dockPos === "right" ? "secondary" : "ghost"} size="icon" className="h-7 w-7" title="Belah kanan" data-testid="trx-dock-right" onClick={() => setDock("right")}><PanelRight className="h-4 w-4" /></Button>
                    <Button type="button" variant={dockPos === "bottom" ? "secondary" : "ghost"} size="icon" className="h-7 w-7" title="Belah bawah" data-testid="trx-dock-bottom" onClick={() => setDock("bottom")}><PanelBottom className="h-4 w-4" /></Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeForm}>Tutup</Button>
                </div>
              </div>
              <TrxForm
                key="docked"
                apps={apps} tabs={tabs} cats={cats}
                onCreated={load} onClose={closeForm}
                rapid={true} bottomDock={dockPos === "bottom"}
              />
            </div>
          </div>
        )}
      </div>


      {/* Pop-up EDIT terpisah — tidak mengganggu form transaksi baru yang sedang berlangsung */}
      <Dialog open={editOpen} onOpenChange={(o) => (o ? null : closeEdit())}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="trx-edit-dialog">
          <DialogHeader><DialogTitle>Edit Transaksi {editingTrx?.trx_no || ""}</DialogTitle></DialogHeader>
          {editingTrx && (
            <div className="mt-2">
              <TrxForm
                key={`edit-${editingTrx.id}`}
                apps={apps} tabs={tabs} cats={cats} editingTrx={editingTrx}
                onCreated={load} onClose={closeEdit}
                rapid={false}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CatalogManager open={manage} onClose={() => setManage(false)} apps={apps} tabs={tabs} cats={cats} onChanged={loadCatalog} />
      <ImportDialog open={openImport} onClose={() => setOpenImport(false)} onDone={load} />
    </div>
  );
}

function TrxForm({ apps, tabs, cats, onCreated, onClose, rapid, bottomDock = false, editingTrx }) {
  const open = true; // komponen hanya dirender saat form terlihat
  const isEditing = !!editingTrx;
  const base = {
    tab_id: "", category_id: "", app_id: "", account_id: "",
    nominal: "", biaya_admin: "", harga_modal: "", harga_jual: "",
    transfer_method: "", transfer_category: "", potongan: "",
    cash_received: "", payment_method: "tunai", notes: "", customer_id: "", affect_balance: true,
    is_operational: false, operational_note: "",
  };
  const [form, setForm] = useState(base);
  const [calcMode, setCalcMode] = useState("admin"); // "admin" | "beli"
  const [subtab, setSubtab] = useState("");
  const [dt, setDt] = useState(wibNowLocal());
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paidTouched, setPaidTouched] = useState(false);
  const [adminTouched, setAdminTouched] = useState(false);
  const [tiers, setTiers] = useState({ normal: [], potongan: [] });
  const [customers, setCustomers] = useState([]);
  const [openCustForm, setOpenCustForm] = useState(false);
  const [opNotes, setOpNotes] = useState([]);
  const [manageOp, setManageOp] = useState(false);
  const loadOpNotes = () => api.get("/catalog/operational-notes").then((r) => setOpNotes(r.data)).catch(() => {});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const readyRef = useRef(!editingTrx); // saat edit: false dulu sampai hydrasi selesai (cegah efek reset menimpa nilai)

  const loadCustomers = () => api.get("/customers").then((r) => setCustomers(r.data));

  const groupsOf = (tabId) => [...new Set(cats.filter((c) => c.tab_id === tabId).map((c) => c.group).filter(Boolean))];

  useEffect(() => {
    if (open) {
      if (editingTrx) {
        const t = editingTrx;
        setForm({
          tab_id: t.tab_id || "", category_id: t.category_id || "", app_id: t.app_id || "", account_id: t.account_id || "",
          nominal: t.nominal ?? "", biaya_admin: t.biaya_admin ?? "", harga_modal: t.harga_modal ?? "", harga_jual: t.harga_jual ?? "",
          transfer_method: t.transfer_method || "", transfer_category: t.transfer_category || "", potongan: t.potongan ?? "",
          cash_received: t.cash_received ?? "", payment_method: t.payment_method || "tunai",
          notes: t.notes || "", customer_id: t.customer_id || "", affect_balance: t.affect_balance !== false,
          is_operational: !!t.is_operational, operational_note: t.operational_note || "",
        });
        const tabObj = tabs.find((x) => x.id === t.tab_id);
        setCalcMode(t.mode || tabObj?.mode || "admin");
        const g = groupsOf(t.tab_id);
        let sub = "";
        const fl = (t.flow || "").toLowerCase();
        if (fl === "tarik") sub = g.find((x) => x.toLowerCase().includes("tarik")) || g[0] || "";
        else if (fl === "kirim") sub = g.find((x) => !x.toLowerCase().includes("tarik")) || g[g.length - 1] || "";
        else { const c = cats.find((x) => x.id === t.category_id); sub = c?.group || g[0] || ""; }
        setSubtab(sub);
        try {
          const d = new Date(new Date(t.date).getTime() + 7 * 3600 * 1000);
          setDt(d.toISOString().slice(0, 16));
        } catch { setDt(wibNowLocal()); }
        setAdminTouched(true);
        setPaidTouched(true);
        setTimeout(() => { readyRef.current = true; }, 0);
      } else {
        const firstTab = tabs.find((t) => /tarik|kirim/i.test(t.name)) || tabs.find((t) => t.no_payment) || tabs[0];
        const g = groupsOf(firstTab?.id);
        const kirimGroup = g.find((x) => /kirim/i.test(x)) || g[0] || "";
        setForm({ ...base, tab_id: firstTab?.id || "" });
        setCalcMode(firstTab?.mode || "admin");
        setSubtab(kirimGroup);
        setDt(wibNowLocal());
        setPaidTouched(false);
        setAdminTouched(false);
      }
      setPreview(null);
      loadCustomers();
      loadOpNotes();
      api.get("/settings").then((r) => setTiers({
        normal: r.data?.admin_tiers_normal || [],
        potongan: r.data?.admin_tiers_potongan || [],
      })).catch(() => {});
    }
    // eslint-disable-next-line
  }, [open]);

  // Pastikan tab default selalu terpilih begitu daftar tab termuat (hindari kondisi "tanpa tab").
  useEffect(() => {
    if (open && !editingTrx && !form.tab_id && tabs.length > 0) {
      const t = tabs.find((x) => /tarik|kirim/i.test(x.name)) || tabs.find((x) => x.no_payment) || tabs[0];
      const g = groupsOf(t?.id);
      setForm((f) => ({ ...f, tab_id: t?.id || "" }));
      setCalcMode(t?.mode || "admin");
      setSubtab(g.find((x) => /kirim/i.test(x)) || g[0] || "");
    }
    // eslint-disable-next-line
  }, [open, tabs, form.tab_id]);

  const tabCats = cats.filter((c) => c.tab_id === form.tab_id);
  const groups = [...new Set(tabCats.map((c) => c.group).filter(Boolean))];
  const visibleCats = groups.length ? tabCats.filter((c) => (c.group || "") === subtab) : tabCats;
  const noPayment = !!tabs.find((t) => t.id === form.tab_id)?.no_payment;

  // Konteks: tab Tarik/Kirim (no_payment). Sub-tab Tarik vs Kirim.
  const isTarik = (subtab || "").toLowerCase().includes("tarik");
  const isBonus = !!tabs.find((t) => t.id === form.tab_id)?.is_bonus; // tab Bonus/Pemasukan
  const isKirimNoPay = noPayment && !isTarik && !isBonus; // sub-tab Kirim pada tab tanpa metode bayar
  const selectedApp = apps.find((a) => a.id === form.app_id);
  // Kategori metode berasal dari APLIKASI (khusus Kirim). Tiap kategori boleh punya sub-metode + potongan.
  const feeCats = selectedApp?.fee_categories || [];
  const selectedFeeCat = feeCats.find((c) => c.name === form.transfer_category);
  const feeCatMethods = selectedFeeCat?.methods || [];
  const showFeeMethod = isKirimNoPay && !!form.app_id && !!form.transfer_category && feeCatMethods.length > 0;
  // Kategori transaksi lama (trx_categories) TIDAK dipakai untuk sub-tab Kirim (diganti kategori dari aplikasi)
  const showCat = !isKirimNoPay && !isBonus && visibleCats.length !== 1; // sembunyikan bila cuma 1 kategori (otomatis dipilih)

  // Pilih kategori metode (dari aplikasi). Reset metode; potongan 0 bila kategori tanpa sub.
  const onFeeCategory = (name) => {
    const c = feeCats.find((x) => x.name === name);
    const methods = c?.methods || [];
    setForm((f) => ({ ...f, transfer_category: name, transfer_method: "", potongan: methods.length ? "" : 0 }));
  };
  const onTransferMethod = (m) => {
    const fee = feeCatMethods.find((f) => f.method === m)?.fee ?? 0;
    setForm((f) => ({ ...f, transfer_method: m, potongan: fee }));
  };

  // Bersihkan kategori/metode/potongan bila konteksnya bukan Kirim (mis. pindah ke Tarik / Beli / Pembayaran)
  useEffect(() => {
    if (isEditing && !readyRef.current) return; // jangan timpa saat hydrasi edit
    if (!isKirimNoPay && (form.potongan !== "" || form.transfer_method || form.transfer_category)) {
      setForm((f) => ({ ...f, potongan: "", transfer_method: "", transfer_category: "" }));
    }
    // eslint-disable-next-line
  }, [isKirimNoPay]);

  // Aplikasi berubah -> pilih akun pertama (bila app punya akun) atau kosongkan. Jangan reset saat hidrasi edit.
  useEffect(() => {
    if (isEditing && !readyRef.current) return;
    const accs = (apps.find((a) => a.id === form.app_id)?.accounts) || [];
    const firstId = accs.length ? accs[0].id : "";
    setForm((f) => (f.account_id === firstId || accs.some((a) => a.id === f.account_id) ? f : { ...f, account_id: firstId }));
    // eslint-disable-next-line
  }, [form.app_id]);

  // Aplikasi berubah pada konteks Kirim -> reset kategori/metode/potongan (kategori berbeda tiap aplikasi)
  useEffect(() => {
    if (isEditing && !readyRef.current) return; // jangan reset saat hydrasi edit
    if (!isKirimNoPay) return;
    setForm((f) => ({ ...f, transfer_category: "", transfer_method: "", potongan: "" }));
    // eslint-disable-next-line
  }, [form.app_id]);

  // Tarik hanya boleh metode "Admin Manual"
  useEffect(() => {
    if (isTarik && calcMode !== "admin") setCalcMode("admin");
    // eslint-disable-next-line
  }, [isTarik]);

  // Biaya admin OTOMATIS dari tabel tarif bertingkat (mode admin). Pakai tabel "potongan" bila ada potongan (antar bank), selain itu tabel normal. Tetap bisa diedit manual.
  // KHUSUS metode yang ditandai "no_potongan" (mis. Sesama) ATAU kategori "admin tanpa potongan": biaya admin tetap pakai tabel NORMAL walau ada potongan.
  const isSesama = (form.transfer_method || "").toLowerCase().includes("sesama");
  const selMethod = feeCatMethods.find((m) => m.method === form.transfer_method);
  const methodNoPotongan = !!selMethod?.no_potongan || isSesama;
  const catNoPotongan = !!selectedFeeCat?.admin_no_potongan;
  const usePotonganTier = Number(toNum(form.potongan)) > 0 && !methodNoPotongan && !catNoPotongan;
  const computeAdminTier = (nominalVal) => {
    const table = (usePotonganTier ? tiers.potongan : tiers.normal) || [];
    if (!table.length || !(nominalVal > 0)) return null;
    const sorted = [...table].sort((a, b) => Number(a.up_to) - Number(b.up_to));
    for (const t of sorted) if (nominalVal < Number(t.up_to)) return Number(t.fee);
    return Number(sorted[sorted.length - 1].fee); // di atas tingkat tertinggi -> pakai tarif tertinggi
  };
  useEffect(() => {
    if (calcMode !== "admin" || adminTouched || isBonus) return;
    // Transaksi operasional: tidak ada biaya admin.
    if (form.is_operational) {
      if (Number(toNum(form.biaya_admin)) !== 0) set("biaya_admin", 0);
      return;
    }
    const nominalVal = toNum(form.nominal);
    const fee = computeAdminTier(nominalVal);
    if (fee != null && Number(toNum(form.biaya_admin)) !== fee) set("biaya_admin", fee);
    // eslint-disable-next-line
  }, [form.nominal, form.is_operational, calcMode, adminTouched, usePotonganTier, tiers]);

  // Auto-pilih kategori bila sub-tab/tab ini hanya punya satu kategori (hindari pilih dua kali)
  useEffect(() => {
    if (!open) return;
    if (isEditing && !readyRef.current) return; // jangan ubah kategori saat hydrasi edit
    if (isKirimNoPay) return; // Kirim pakai kategori dari aplikasi, bukan trx_categories
    if (visibleCats.length === 1) {
      const only = visibleCats[0];
      if (form.category_id !== only.id) {
        setForm((f) => ({ ...f, category_id: only.id, app_id: only.default_app_id || f.app_id }));
      }
    } else if (form.category_id && !visibleCats.some((c) => c.id === form.category_id)) {
      setForm((f) => ({ ...f, category_id: "" }));
    }
    // eslint-disable-next-line
  }, [form.tab_id, subtab, cats, open]);

  const totalBayar = Number(preview?.total_bayar || 0);
  const paid = toNum(form.cash_received);
  const kurang = !noPayment ? Math.max(0, Math.round(totalBayar - paid)) : 0;
  const kembalian = !noPayment && form.payment_method === "tunai" ? Math.max(0, paid - totalBayar) : 0;
  const selectedCust = customers.find((c) => c.id === form.customer_id);

  // Auto-isi "Uang Dibayar" = total bayar (lunas) selama user belum mengubahnya
  useEffect(() => {
    if (!open || noPayment) return;
    if (!paidTouched && totalBayar > 0 && Number(form.cash_received) !== totalBayar) {
      set("cash_received", totalBayar);
    }
    // eslint-disable-next-line
  }, [totalBayar, open, noPayment, paidTouched]);

  const onTab = (id) => {
    const t = tabs.find((x) => x.id === id);
    const g = groupsOf(id);
    setForm((f) => ({ ...f, tab_id: id, category_id: "", app_id: "" }));
    setCalcMode(t?.mode || "admin");
    setSubtab(g[0] || "");
    setAdminTouched(false);
  };
  const onCat = (id) => {
    const c = cats.find((x) => x.id === id);
    setForm((f) => ({ ...f, category_id: id, app_id: c?.default_app_id || f.app_id }));
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.post("/transactions/preview", {
        ...form, mode: calcMode,
        nominal: toNum(form.nominal), biaya_admin: toNum(form.biaya_admin),
        harga_modal: toNum(form.harga_modal), harga_jual: toNum(form.harga_jual),
        potongan: toNum(form.potongan),
        cash_received: toNum(form.cash_received),
      }).then((r) => setPreview(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form, calcMode, open]);

  const submit = async () => {
    if (!form.tab_id) return toast.error("Pilih tab");
    if (isBonus) {
      if (!form.app_id) return toast.error("Pilih aplikasi tujuan bonus");
      if (!form.biaya_admin) return toast.error("Isi nominal bonus");
    } else {
      if (!isKirimNoPay && !form.category_id) return toast.error("Pilih kategori");
      if (isKirimNoPay && feeCats.length > 0 && !form.transfer_category) return toast.error("Pilih kategori metode");
      if (calcMode === "beli" && !form.harga_jual) return toast.error("Isi harga bayar");
      if (calcMode === "admin" && !form.nominal) return toast.error("Isi nominal");
    }
    if (kurang > 0 && !form.customer_id) return toast.error("Kurang bayar: pilih pelanggan (utang ke siapa)");
    if (selectedApp?.accounts?.length > 0 && !form.account_id) return toast.error(`Pilih akun ${selectedApp.name}`);
    setSaving(true);
    try {
      const payload = {
        ...form, mode: calcMode,
        flow: isBonus ? "bonus" : (noPayment ? (isTarik ? "tarik" : "kirim") : null),
        nominal: isBonus ? 0 : toNum(form.nominal),
        biaya_admin: (!isBonus && form.is_operational) ? 0 : toNum(form.biaya_admin),
        harga_modal: toNum(form.harga_modal), harga_jual: toNum(form.harga_jual),
        potongan: isBonus ? 0 : toNum(form.potongan),
        transfer_method: isBonus ? null : (form.transfer_method || null),
        transfer_category: isBonus ? "Bonus" : (form.transfer_category || null),
        category_id: (isKirimNoPay || isBonus) ? null : (form.category_id || null),
        account_id: form.account_id || null,
        cash_received: toNum(form.cash_received),
        payment_method: noPayment ? null : form.payment_method,
        amount_paid: noPayment ? null : paid,
        customer_id: form.customer_id || null,
        is_operational: isBonus ? false : !!form.is_operational,
        operational_note: (!isBonus && form.is_operational) ? (form.operational_note || null) : null,
        date: wibLocalToUTCISO(dt),
      };
      if (!noPayment && payload.payment_method === "tunai" && kurang === 0 && preview?.kembalian_suggestion) {
        payload.cash_denom_return = preview.kembalian_suggestion.denominations;
      }
      if (isEditing) {
        await api.put(`/transactions/${editingTrx.id}`, payload);
        toast.success("Perubahan tersimpan ✓");
        onCreated?.();
        onClose?.();
        return;
      }
      await api.post("/transactions", payload);
      toast.success(kurang > 0 ? `Tersimpan · utang Rp ${kurang.toLocaleString("id-ID")} dicatat ✓` : "Transaksi tersimpan ✓");
      onCreated?.();
      if (rapid) {
        // Mode beruntun: pertahankan SEMUA setelan (tab, kategori, aplikasi, tanggal, metode, mode data lama),
        // hanya kosongkan angka & pelanggan, lalu fokus balik ke kolom nominal.
        setForm((f) => ({ ...f, nominal: "", biaya_admin: "", harga_modal: "", harga_jual: "", cash_received: "", customer_id: "" }));
        setPaidTouched(false);
        setAdminTouched(false);
        setPreview(null);
        setTimeout(() => {
          const el = document.querySelector('[data-testid="new-trx-nominal"], [data-testid="new-trx-modal"]');
          if (el) { el.focus(); el.select?.(); }
        }, 60);
      } else {
        onClose?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan transaksi");
    } finally { setSaving(false); }
  };

  const onAmountKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); submit(); }
  };

  // Blok field dipakai ulang (single-flow kanan/mobile & kartu belah bawah).
  // Di dalam kartu tetap 2 item/baris bila muat (jangan dipaksa 1 per baris).
  const pairCols = "grid-cols-2";
  const blkTab = (
    <div>
      <Label>Tab</Label>
      <Tabs value={form.tab_id} onValueChange={onTab}>
        <TabsList className="flex-wrap h-auto">
          {tabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id} data-testid={`trx-tab-${t.id}`}>{t.name}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
  const blkJenis = groups.length > 0 ? (
    <div>
      <Label>Jenis</Label>
      <Tabs value={subtab} onValueChange={(v) => { setSubtab(v); set("category_id", ""); setAdminTouched(false); }}>
        <TabsList className="flex-wrap h-auto">
          {groups.map((g) => <TabsTrigger key={g} value={g} data-testid={`trx-subtab-${g}`}>{g}</TabsTrigger>)}
        </TabsList>
      </Tabs>
    </div>
  ) : null;
  const blkMetodeHitung = (!isTarik && !isBonus) ? (
    <div>
      <Label>Metode Hitung</Label>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" data-testid="calc-mode-admin" variant={calcMode === "admin" ? "default" : "outline"} onClick={() => setCalcMode("admin")} className="h-auto py-2 flex-col gap-0.5">
          <span className="text-xs font-semibold">Admin Manual</span>
          <span className="text-[10px] opacity-70">Nominal + Biaya Admin</span>
        </Button>
        <Button type="button" data-testid="calc-mode-beli" variant={calcMode === "beli" ? "default" : "outline"} onClick={() => setCalcMode("beli")} className="h-auto py-2 flex-col gap-0.5">
          <span className="text-xs font-semibold">Hitung Otomatis</span>
          <span className="text-[10px] opacity-70">Modal + Harga Bayar</span>
        </Button>
      </div>
    </div>
  ) : null;
  const blkKatApp = (
    <div className={`grid gap-3 ${showCat ? pairCols : "grid-cols-1"}`}>
      {showCat && (
      <div>
        <Label>Kategori</Label>
        <Select value={form.category_id} onValueChange={onCat}>
          <SelectTrigger data-testid="new-trx-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
          <SelectContent>
            {visibleCats.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">Belum ada kategori</div>}
            {visibleCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      )}
      <div>
        <Label>Aplikasi</Label>
        <Select value={form.app_id || "none"} onValueChange={(v) => set("app_id", v === "none" ? "" : v)}>
          <SelectTrigger data-testid="new-trx-app"><SelectValue placeholder="Pilih aplikasi" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Tanpa aplikasi —</SelectItem>
            {apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
  const blkAkun = selectedApp?.accounts?.length > 0 ? (
    <div data-testid="new-trx-account-wrap">
      <Label>Akun {selectedApp.name}</Label>
      <Select value={form.account_id || ""} onValueChange={(v) => set("account_id", v)}>
        <SelectTrigger data-testid="new-trx-account"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
        <SelectContent>
          {selectedApp.accounts.map((ac) => (
            <SelectItem key={ac.id} value={ac.id}>{ac.label} · {rupiah(ac.saldo || 0)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-muted-foreground mt-1">Saldo akun yang dipilih inilah yang akan berubah.</p>
    </div>
  ) : null;
  const blkFee = (isKirimNoPay && !!form.app_id && feeCats.length > 0) ? (
    <div className="space-y-3 rounded-lg border p-3 bg-accent/30">
      <div>
        <Label>Kategori</Label>
        <Select value={form.transfer_category || ""} onValueChange={onFeeCategory}>
          <SelectTrigger data-testid="new-trx-fee-category"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
          <SelectContent>
            {feeCats.map((c) => (
              <SelectItem key={c.name} value={c.name}>
                {c.name}{(c.methods?.length || 0) === 0 ? "" : ` · ${c.methods.length} metode`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {showFeeMethod && (
        <div className={`grid gap-3 ${pairCols}`}>
          <div>
            <Label>Tipe / Metode</Label>
            <Select value={form.transfer_method || ""} onValueChange={onTransferMethod}>
              <SelectTrigger data-testid="new-trx-transfer-method"><SelectValue placeholder="Pilih metode" /></SelectTrigger>
              <SelectContent>
                {feeCatMethods.map((f) => <SelectItem key={f.method} value={f.method}>{f.method} · {rupiah(f.fee)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Potongan</Label>
            <MoneyInput data-testid="new-trx-potongan" value={form.potongan} onChange={(v) => set("potongan", v)} onKeyDown={onAmountKey} />
            <p className="text-[11px] text-muted-foreground mt-1">Mengurangi saldo aplikasi & keuntungan.</p>
          </div>
        </div>
      )}
      {!!form.transfer_category && feeCatMethods.length === 0 && (
        <p className="text-[11px] text-muted-foreground">Kategori ini tanpa sub-metode & tanpa potongan.</p>
      )}
    </div>
  ) : null;
  const blkOperational = isKirimNoPay ? (
    <div className="rounded-lg border p-3 space-y-2 border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/40 dark:bg-indigo-500/5">
      <label className="flex items-center justify-between gap-2 cursor-pointer">
        <span className="text-sm font-medium flex items-center gap-1.5">
          <Wrench className="h-4 w-4 text-indigo-500" /> Transaksi Operasional Toko
        </span>
        <Switch data-testid="new-trx-operational" checked={!!form.is_operational} onCheckedChange={(v) => { set("is_operational", v); if (v) { setAdminTouched(false); set("biaya_admin", 0); } }} />
      </label>
      {form.is_operational && (
        <div className="space-y-2 rounded-md bg-indigo-100/60 dark:bg-indigo-500/10 p-2.5">
          <p className="text-[11px] text-indigo-700 dark:text-indigo-300 leading-snug">Untuk kebutuhan toko sendiri: <b>tanpa biaya admin</b>, potongan tetap tercatat & mengurangi saldo aplikasi.</p>
          {!form.app_id && <p className="text-[11px] text-amber-600 dark:text-amber-400">Pilih Aplikasi & metode di atas agar potongan ikut terhitung.</p>}
          <div>
            <div className="flex items-center justify-between">
              <Label>Keterangan Operasional</Label>
              <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px] gap-1" onClick={() => setManageOp(true)} data-testid="btn-manage-op-notes"><Settings2 className="h-3 w-3" /> Kelola</Button>
            </div>
            <Select value={form.operational_note || ""} onValueChange={(v) => set("operational_note", v)}>
              <SelectTrigger data-testid="new-trx-op-note"><SelectValue placeholder="Pilih keterangan" /></SelectTrigger>
              <SelectContent>
                {opNotes.length === 0 && <div className="px-2 py-2 text-xs text-muted-foreground">Belum ada keterangan. Klik &quot;Kelola&quot;.</div>}
                {opNotes.map((n) => <SelectItem key={n.id} value={n.name}>{n.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  ) : null;
  const blkAmounts = isBonus ? (
    <div>
      <Label>Nominal Bonus</Label>
      <MoneyInput data-testid="new-trx-bonus" value={form.biaya_admin} onChange={(v) => { setAdminTouched(true); set("biaya_admin", v); }} onKeyDown={onAmountKey} />
      <p className="text-[11px] text-muted-foreground mt-1">Menambah saldo aplikasi terpilih & dihitung sebagai keuntungan penuh (tanpa modal, tanpa kas).</p>
    </div>
  ) : calcMode === "beli" ? (
    <div className={`grid gap-3 ${pairCols}`}>
      <div><Label>Harga Modal</Label><MoneyInput data-testid="new-trx-modal" value={form.harga_modal} onChange={(v) => set("harga_modal", v)} onKeyDown={onAmountKey} /></div>
      <div><Label>Harga Bayar</Label><MoneyInput data-testid="new-trx-jual" value={form.harga_jual} onChange={(v) => set("harga_jual", v)} onKeyDown={onAmountKey} /></div>
    </div>
  ) : (
    <div className={`grid gap-3 ${pairCols}`}>
      <div><Label>Nominal</Label><MoneyInput data-testid="new-trx-nominal" value={form.nominal} onChange={(v) => set("nominal", v)} onKeyDown={onAmountKey} /></div>
      {form.is_operational ? (
        <div><Label>Biaya Admin</Label><div className="h-10 flex items-center px-3 rounded-md border bg-muted/40 text-sm text-muted-foreground" data-testid="new-trx-admin-operational">Rp 0 · operasional</div><p className="text-[11px] text-muted-foreground mt-1">Transaksi operasional: tanpa biaya admin.</p></div>
      ) : (
        <div><Label>Biaya Admin (untung)</Label><MoneyInput data-testid="new-trx-admin" value={form.biaya_admin} onChange={(v) => { setAdminTouched(true); set("biaya_admin", v); }} onKeyDown={onAmountKey} /><p className="text-[11px] text-muted-foreground mt-1">Terisi otomatis dari tabel tarif, bisa diubah.</p></div>
      )}
    </div>
  );
  const blkPayment = !noPayment ? (
    <>
      <div className={`grid gap-3 ${pairCols}`}>
        <div>
          <Label>Metode Pembayaran</Label>
          <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
            <SelectTrigger data-testid="new-trx-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Uang Dibayar</Label>
          <MoneyInput data-testid="new-trx-cash" value={form.cash_received} onChange={(v) => { setPaidTouched(true); set("cash_received", v); }} onKeyDown={onAmountKey} />
          <p className="text-[11px] text-muted-foreground mt-1">Isi jumlah yang dibayar pelanggan. Kurang dari total = utang.</p>
        </div>
      </div>
      {kurang > 0 && (
        <Card className="p-3 border-rose-300 bg-rose-50 dark:bg-rose-500/10 dark:border-rose-800 space-y-2.5">
          <p className="text-sm font-semibold text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
            <AlertTriangle className="h-4 w-4" /> Kurang bayar {rupiah(kurang)} → dicatat sebagai UTANG
          </p>
          <div>
            <Label>Utang atas nama (pelanggan) *</Label>
            <div className="flex gap-2">
              <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
                <SelectTrigger data-testid="trx-debt-customer" className="flex-1"><SelectValue placeholder="Pilih pelanggan..." /></SelectTrigger>
                <SelectContent>
                  {customers.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">Belum ada pelanggan</div>}
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="icon" data-testid="btn-quick-add-customer" onClick={() => setOpenCustForm(true)} title="Tambah pelanggan baru"><UserPlus className="h-4 w-4" /></Button>
            </div>
            {selectedCust && (
              <p className="text-[11px] text-muted-foreground mt-1">
                {selectedCust.name}{selectedCust.phone ? ` · WA ${selectedCust.phone}` : " · tanpa nomor WA"}
              </p>
            )}
          </div>
        </Card>
      )}
    </>
  ) : null;
  const blkTanggalJam = (
    <>
      <div className={`grid gap-3 ${pairCols}`}>
        <div>
          <Label>Tanggal (WIB)</Label>
          <Input data-testid="new-trx-date" type="date" value={(dt.split("T")[0]) || ""} onChange={(e) => setDt(`${e.target.value}T${dt.split("T")[1] || "00:00"}`)} />
        </div>
        <div>
          <Label>Jam (WIB)</Label>
          <Input data-testid="new-trx-time" type="time" value={(dt.split("T")[1] || "").slice(0, 5)} onChange={(e) => setDt(`${dt.split("T")[0]}T${e.target.value}`)} />
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground -mt-1">Otomatis terisi tanggal & jam sekarang (WIB), bisa diubah manual.</p>
    </>
  );
  const blkAffect = (
    <div className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${!form.affect_balance ? "bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-800" : "bg-accent/40 border-border"}`}>
      <div className="min-w-0">
        <p className="text-sm font-medium">Kurangi saldo aplikasi & kas</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {form.affect_balance
            ? "Transaksi berjalan (Agustus dst): saldo otomatis berkurang."
            : "MODE DATA LAMA: untung tetap masuk laporan sesuai tanggal, TAPI saldo aplikasi & kas TIDAK diubah. Cocok untuk melengkapi data Juli."}
        </p>
      </div>
      <Switch data-testid="new-trx-affect-balance" checked={form.affect_balance} onCheckedChange={(v) => set("affect_balance", v)} />
    </div>
  );
  const blkCatatan = (
    <div><Label>Catatan</Label><Textarea data-testid="new-trx-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
  );
  const blkEditBanner = isEditing ? (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-accent/40 border-border">
      <div className="min-w-0">
        <p className="text-sm font-medium">Edit Transaksi</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">Ubah nilai lalu simpan perubahan.</p>
      </div>
    </div>
  ) : null;

  const blkSummaryInner = preview ? (
    <>
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1">
        <Sparkles className="h-3 w-3" /> Otomatis Terhitung
      </p>
      {isTarik ? (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>Masuk ke saldo aplikasi:</div>
          <div className="text-right money font-semibold text-emerald-600 dark:text-emerald-400">+{rupiah(preview.nominal)}</div>
          <div>Uang tunai keluar:</div>
          <div className="text-right money font-semibold text-sky-600 dark:text-sky-400">-{rupiah(Math.max(0, Number(preview.nominal || 0) - Number(preview.biaya_admin || 0)))}</div>
          <div>Untung (biaya admin):</div>
          <div className="text-right money font-semibold text-emerald-600 dark:text-emerald-400">{rupiah(preview.keuntungan)}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>Total Bayar:</div>
          <div className="text-right money font-semibold">{rupiah(preview.total_bayar)}</div>
          <div>Keuntungan:</div>
          <div className="text-right money font-semibold text-emerald-600 dark:text-emerald-400">{rupiah(preview.keuntungan)}</div>
          {Number(preview.potongan) > 0 && (
            <>
              <div className="text-rose-600 dark:text-rose-400">Potongan Bank{form.transfer_method ? ` (${form.transfer_method})` : ""}:</div>
              <div className="text-right money font-semibold text-rose-600 dark:text-rose-400">-{rupiah(preview.potongan)}</div>
            </>
          )}
          {!noPayment && (
            <>
              <div>Uang Dibayar:</div>
              <div className="text-right money font-semibold">{rupiah(paid)}</div>
            </>
          )}
          {!noPayment && kembalian > 0 && (
            <>
              <div>Kembalian:</div>
              <div className="text-right money font-semibold text-sky-600 dark:text-sky-400">{rupiah(kembalian)}</div>
            </>
          )}
          {kurang > 0 && (
            <>
              <div className="text-rose-600 dark:text-rose-400 font-semibold">Kurang (Utang):</div>
              <div className="text-right money font-bold text-rose-600 dark:text-rose-400">{rupiah(kurang)}</div>
            </>
          )}
        </div>
      )}
    </>
  ) : null;

  return (
    <>
    <div className={!rapid ? "space-y-4" : "flex flex-col min-h-0 flex-1"}>
      {bottomDock ? (
        <>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-3">
            {blkEditBanner}
            {blkTab}
            {blkJenis}
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-3 auto-rows-min content-start items-start">
              {/* Kolom 1 — Detail */}
              <div className="rounded-lg border bg-card p-3 space-y-3 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Detail</p>
                {blkKatApp}{blkAkun}{blkFee}{blkPayment}
              </div>
              {/* Kolom 2 — Waktu & Lainnya */}
              <div className="rounded-lg border bg-card p-3 space-y-3 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Waktu &amp; Lainnya</p>
                {blkTanggalJam}{blkOperational}{blkAffect}{blkCatatan}
              </div>
              {/* Kolom 3 — Hitung + Otomatis Terhitung (satu kartu) */}
              <div className="rounded-lg border-2 border-dashed border-primary/40 bg-accent/30 p-3 space-y-3 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Sparkles className="h-3 w-3" /> Hitung</p>
                {blkMetodeHitung}
                {blkAmounts}
                {blkSummaryInner && <div className="pt-3 border-t border-dashed border-primary/25">{blkSummaryInner}</div>}
              </div>
            </div>
          </div>
          <div className="shrink-0 border-t bg-background px-4 py-3">
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1">Tutup</Button>
              <Button data-testid="btn-save-trx" onClick={submit} disabled={saving} className="flex-1">{saving ? "Menyimpan..." : (isEditing ? "Simpan Perubahan" : "Simpan & Lanjut")}</Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={rapid ? "flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4 lg:space-y-0 lg:grid lg:grid-cols-1 lg:gap-y-3 lg:h-full lg:content-between" : "space-y-4"}>
            {blkEditBanner}
            {blkTab}
            {blkJenis}
            {blkMetodeHitung}
            {blkKatApp}
            {blkAkun}
            {blkFee}
            {blkOperational}
            {blkAmounts}
            {blkPayment}
            {blkTanggalJam}
            {blkAffect}
            {blkCatatan}
          </div>
          <div className={!rapid ? "contents" : "shrink-0 border-t bg-background px-4 py-3 space-y-3"}>
            {blkSummaryInner && (
              <Card className={`bg-accent/40 border-dashed ${rapid ? "p-3 max-h-44 overflow-y-auto" : "p-4"}`}>
                {blkSummaryInner}
              </Card>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1">{rapid ? "Tutup" : "Batal"}</Button>
              <Button data-testid="btn-save-trx" onClick={submit} disabled={saving} className="flex-1">{saving ? "Menyimpan..." : (isEditing ? "Simpan Perubahan" : (rapid ? "Simpan & Lanjut" : "Simpan"))}</Button>
            </div>
          </div>
        </>
      )}
    </div>
    <CustomerForm open={openCustForm} customer={null} onClose={() => setOpenCustForm(false)} onSaved={async (saved) => { await loadCustomers(); if (saved?.id) set("customer_id", saved.id); }} />
    <OperationalNotesDialog open={manageOp} onClose={() => setManageOp(false)} notes={opNotes} onChanged={loadOpNotes} />
    </>
  );
}

function OperationalNotesDialog({ open, onClose, notes, onChanged }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try { await api.post("/catalog/operational-notes", { name: n }); setName(""); await onChanged?.(); toast.success("Keterangan ditambah"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/catalog/operational-notes/${id}`); await onChanged?.(); }
    catch { toast.error("Gagal menghapus"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Kelola Keterangan Operasional</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input data-testid="op-note-input" placeholder="mis. Beli ATK" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
            <Button data-testid="op-note-add" onClick={add} disabled={busy}>Tambah</Button>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {notes.length === 0 && <p className="text-xs text-muted-foreground">Belum ada keterangan.</p>}
            {notes.map((n) => (
              <div key={n.id} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5" data-testid={`op-note-${n.id}`}>
                <span>{n.name}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => remove(n.id)} data-testid={`op-note-del-${n.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [dataLama, setDataLama] = useState(true); // true = jangan ubah saldo & kas (affect_balance false)
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // preview result
  const [done, setDone] = useState(null); // commit result
  const fileRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); setDone(null); if (fileRef.current) fileRef.current.value = ""; };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/transactions/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "template_import_transaksi.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh template"); }
  };

  const pickFile = (e) => { setFile(e.target.files?.[0] || null); setResult(null); setDone(null); };

  const doPreview = async () => {
    if (!file) { toast.error("Pilih file Excel dulu"); return; }
    setBusy(true); setDone(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("affect_balance", String(!dataLama));
      const r = await api.post("/transactions/import/preview", fd);
      setResult(r.data);
      if (r.data.invalid > 0) toast.warning(`${r.data.invalid} baris bermasalah, ${r.data.valid} siap diimpor`);
      else toast.success(`${r.data.valid} baris siap diimpor`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membaca file"); }
    finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("affect_balance", String(!dataLama));
      const r = await api.post("/transactions/import/commit", fd);
      setDone(r.data);
      toast.success(`${r.data.created} transaksi berhasil diimpor`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal impor"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-600" /> Import Transaksi via Excel</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border p-3 space-y-2 bg-muted/30">
            <p className="text-sm font-medium">1. Unduh template</p>
            <p className="text-xs text-muted-foreground">Kolom sudah dilengkapi <b>dropdown</b> (Tab, Kategori, Aplikasi, Metode, dll) — cukup pilih. Lihat sheet <b>Contoh</b> & <b>Referensi</b>.</p>
            <Button variant="outline" size="sm" className="gap-2" onClick={downloadTemplate} data-testid="import-download-template"><Download className="h-4 w-4" /> Unduh Template Excel</Button>
          </div>

          <div className="rounded-lg border p-3 space-y-3">
            <p className="text-sm font-medium">2. Upload file yang sudah diisi</p>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={pickFile} data-testid="import-file-input"
              className="block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:opacity-90" />
            <label className="flex items-center justify-between gap-2 cursor-pointer rounded-md bg-amber-50 dark:bg-amber-500/10 p-2.5">
              <span className="text-xs text-amber-800 dark:text-amber-300 leading-snug">
                <b>Data lama (backfill)</b> — hanya catat transaksi, <b>jangan ubah</b> saldo aplikasi & kas saat ini. Matikan bila ingin saldo ikut berubah.
              </span>
              <Switch checked={dataLama} onCheckedChange={setDataLama} data-testid="import-data-lama" />
            </label>
            <Button className="gap-2" onClick={doPreview} disabled={!file || busy} data-testid="import-preview-btn"><Upload className="h-4 w-4" /> {busy && !done ? "Memproses…" : "Pratinjau"}</Button>
          </div>

          {result && (
            <div className="rounded-lg border p-3 space-y-3" data-testid="import-preview-result">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium"><CheckCircle2 className="h-4 w-4" /> {result.valid} valid</span>
                <span className="inline-flex items-center gap-1.5 text-destructive font-medium"><XCircle className="h-4 w-4" /> {result.invalid} bermasalah</span>
                <span className="text-muted-foreground">Total baris: {result.total}</span>
                <span className="text-muted-foreground">Perkiraan untung: <b className="text-foreground">{rupiah(result.totals?.keuntungan || 0)}</b></span>
              </div>
              <div className="max-h-72 overflow-auto rounded border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/60 sticky top-0">
                    <tr>
                      <th className="text-left p-1.5">#</th>
                      <th className="text-left p-1.5">Status</th>
                      <th className="text-left p-1.5">Tab / Kategori</th>
                      <th className="text-right p-1.5">Nominal</th>
                      <th className="text-right p-1.5">Untung</th>
                      <th className="text-left p-1.5">Catatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={r.row} className={`border-t ${r.ok ? "" : "bg-destructive/5"}`}>
                        <td className="p-1.5 align-top">{r.row}</td>
                        <td className="p-1.5 align-top">{r.ok ? <span className="text-emerald-600">OK</span> : <span className="text-destructive">Error</span>}</td>
                        <td className="p-1.5 align-top">{r.ok ? `${r.tab} · ${r.kategori}${r.operasional ? " · Operasional" : ""}` : (r.tab || "-")}</td>
                        <td className="p-1.5 align-top text-right">{r.ok ? rupiah(r.nominal) : "-"}</td>
                        <td className="p-1.5 align-top text-right">{r.ok ? rupiah(r.keuntungan) : "-"}</td>
                        <td className="p-1.5 align-top text-destructive">{r.ok ? "" : (r.errors || []).join("; ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!done && (
                <Button className="gap-2" onClick={doCommit} disabled={busy || result.valid === 0} data-testid="import-commit-btn">
                  <CheckCircle2 className="h-4 w-4" /> {busy ? "Mengimpor…" : `Import ${result.valid} Transaksi`}
                </Button>
              )}
            </div>
          )}

          {done && (
            <div className="rounded-lg border p-3 bg-emerald-50 dark:bg-emerald-500/10 space-y-1" data-testid="import-done">
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Selesai: {done.created} transaksi diimpor{done.failed ? `, ${done.failed} gagal` : ""}.</p>
              {done.failed > 0 && <p className="text-xs text-muted-foreground">Baris gagal: {done.errors.map((e) => `#${e.row}`).join(", ")}</p>}
              <Button size="sm" variant="outline" className="mt-1" onClick={() => { reset(); onClose(); }}>Tutup</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function CatalogManager({ open, onClose, apps, tabs, cats, onChanged }) {
  const [tabForm, setTabForm] = useState(null); // {id?, name, mode}
  const [catForm, setCatForm] = useState(null); // {id?, tab_id, name, default_app_id, group}
  const appName = (id) => apps.find((a) => a.id === id)?.name;

  const saveTab = async () => {
    if (!tabForm.name) return toast.error("Isi nama tab");
    if (tabForm.id) await api.patch(`/catalog/tabs/${tabForm.id}`, { name: tabForm.name, mode: tabForm.mode, no_payment: !!tabForm.no_payment, order: tabForm.order || 0 });
    else await api.post("/catalog/tabs", { name: tabForm.name, mode: tabForm.mode, no_payment: !!tabForm.no_payment, order: tabs.length });
    toast.success("Tab disimpan"); setTabForm(null); onChanged?.();
  };
  const delTab = async (t) => {
    if (!(await confirmDialog({ title: `Hapus tab "${t.name}"?`, description: "Semua kategori di dalam tab ini juga akan dihapus." }))) return;
    await api.delete(`/catalog/tabs/${t.id}`); toast.success("Tab dihapus"); onChanged?.();
  };
  const saveCat = async () => {
    if (!catForm.name) return toast.error("Isi nama kategori");
    const body = { tab_id: catForm.tab_id, name: catForm.name, default_app_id: catForm.default_app_id || null, group: catForm.group || null, order: catForm.order || 0 };
    if (catForm.id) await api.patch(`/catalog/categories/${catForm.id}`, body);
    else await api.post("/catalog/categories", body);
    toast.success("Kategori disimpan"); setCatForm(null); onChanged?.();
  };
  const delCat = async (c) => {
    if (!(await confirmDialog({ title: `Hapus kategori "${c.name}"?` }))) return;
    await api.delete(`/catalog/categories/${c.id}`); toast.success("Kategori dihapus"); onChanged?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Kelola Tab & Kategori</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button size="sm" data-testid="btn-add-tab" onClick={() => setTabForm({ name: "", mode: "admin", no_payment: false })} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> Tambah Tab
            </Button>
          </div>

          {tabs.map((t) => (
            <Card key={t.id} className="p-4" data-testid={`catalog-tab-${t.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  <p className="font-semibold">{t.name}</p>
                  <Badge variant="secondary">{t.mode === "beli" ? "Default: Otomatis" : "Default: Admin"}</Badge>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" data-testid={`btn-edit-tab-${t.id}`} onClick={() => setTabForm(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`btn-del-tab-${t.id}`} onClick={() => delTab(t)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 pl-6">
                {cats.filter((c) => c.tab_id === t.id).map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5" data-testid={`catalog-cat-${c.id}`}>
                    <span className="flex items-center gap-1.5">
                      {c.group && <Badge variant="outline" className="text-[10px]">{c.group}</Badge>}
                      {c.name}
                      {c.default_app_id && <span className="text-xs text-muted-foreground">· {appName(c.default_app_id)}</span>}
                    </span>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`btn-edit-cat-${c.id}`} onClick={() => setCatForm(c)}><Pencil className="h-3 w-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`btn-del-cat-${c.id}`} onClick={() => delCat(c)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="mt-2 gap-1" data-testid={`btn-add-cat-${t.id}`} onClick={() => setCatForm({ tab_id: t.id, name: "", default_app_id: "", group: "" })}>
                  <Plus className="h-3 w-3" /> Kategori
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Tab form */}
        <Dialog open={!!tabForm} onOpenChange={(o) => (o ? null : setTabForm(null))}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{tabForm?.id ? "Edit Tab" : "Tab Baru"}</DialogTitle></DialogHeader>
            {tabForm && (
              <div className="space-y-3">
                <div><Label>Nama Tab</Label><Input data-testid="tab-name" value={tabForm.name} onChange={(e) => setTabForm((f) => ({ ...f, name: e.target.value }))} /></div>
                <div>
                  <Label>Metode Hitung Default</Label>
                  <Select value={tabForm.mode} onValueChange={(v) => setTabForm((f) => ({ ...f, mode: v }))}>
                    <SelectTrigger data-testid="tab-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin Manual — Nominal + Biaya Admin</SelectItem>
                      <SelectItem value="beli">Hitung Otomatis — Modal + Harga Bayar</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1">Hanya default; saat input transaksi tetap bisa ganti metode.</p>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="tab-no-payment" checked={!!tabForm.no_payment} onChange={(e) => setTabForm((f) => ({ ...f, no_payment: e.target.checked }))} className="h-4 w-4 rounded border-input" />
                  Tanpa metode pembayaran (mis. Tarik / Kirim uang)
                </label>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setTabForm(null)}>Batal</Button>
                  <Button className="flex-1" data-testid="btn-save-tab" onClick={saveTab}>Simpan</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Category form */}
        <Dialog open={!!catForm} onOpenChange={(o) => (o ? null : setCatForm(null))}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{catForm?.id ? "Edit Kategori" : "Kategori Baru"}</DialogTitle></DialogHeader>
            {catForm && (
              <div className="space-y-3">
                <div><Label>Nama Kategori</Label><Input data-testid="cat-name" value={catForm.name} onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))} /></div>
                <div><Label>Sub-tab / Grup (opsional)</Label><Input data-testid="cat-group" value={catForm.group || ""} onChange={(e) => setCatForm((f) => ({ ...f, group: e.target.value }))} placeholder="mis. Tarik / Kirim" /></div>
                <div>
                  <Label>Aplikasi Default (opsional)</Label>
                  <Select value={catForm.default_app_id || "none"} onValueChange={(v) => setCatForm((f) => ({ ...f, default_app_id: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="cat-app"><SelectValue placeholder="Tanpa default" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Tanpa default —</SelectItem>
                      {apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setCatForm(null)}>Batal</Button>
                  <Button className="flex-1" data-testid="btn-save-cat" onClick={saveCat}>Simpan</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
